import { v4 as guid } from 'uuid'
import { fabric } from 'fabric'
import { pick } from 'lodash-es'
import { getDiscussionDetails } from '@/api/t1/discussion'
import type UserProfile from '@/models/userProfile'
import { whiteboardConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import { generateUserColor } from '@/services/utils'

interface IWbDiscussionIconOptions extends fabric.IRectOptions, IWbObjectProps {
  discussionId?: number
  title?: string
  resolved?: boolean
  comments?: IDiscussionComment[]
  preventUnlock?: boolean
  preventDelete?: boolean
  createdBy?: number | null
}

export default class WbDiscussionIcon extends fabric.Rect implements IWbObject {
  public id: string
  public discussionId?: number
  public title?: string
  public resolved: boolean
  public comments: IDiscussionComment[]
  public type = whiteboardConstants.objectTypes.discussion
  public lock: boolean
  public connectable: boolean
  public editableProps: Record<string, IWbObjectProp> = {}
  public actions: Record<string, IWObjectActions> = {}
  public preventUnlock: boolean
  public preventDelete?: boolean
  public localOnly?: boolean
  public canBeLinkedTo = false
  public canLink = true
  public link?: { id: string, x: number, y: number }
  public excludeFromGroupSelection?: boolean
  public createdBy?: number | null
  private uniqueUsers?: { userId: number, username: string, firstName: string, lastName: string }[] = []

  constructor(opt?: IWbDiscussionIconOptions) {
    const userStore = useUserStore()
    super({ ...opt, height: 30 })
    this.id = opt?.id || guid()
    this.discussionId = opt?.discussionId
    this.title = opt?.title
    this.resolved = opt?.resolved || false
    this.comments = opt?.comments || []
    this.originX = 'center'
    this.originY = 'center'
    this.calcUniqueUsers()
    this.setWidth()
    this.fill = opt?.fill || 'red'
    this.opacity = opt?.opacity || 1
    this.hasControls = opt?.hasControls || false
    this.lock = opt?.lock || false
    this.connectable = true
    this.preventUnlock = opt?.preventUnlock || false
    this.preventDelete = opt?.preventDelete || false
    this.localOnly = opt?.localOnly || false
    this.excludeFromGroupSelection = opt?.excludeFromGroupSelection || false
    this.createdBy = opt?.createdBy || (this.comments.length ? this.comments[0].userId : userStore.userProfile.Id)

    // show lock/unlock only for owner of the comment
    if (this.createdBy === userStore.userProfile.Id) {
      this.editableProps = { lock: { name: 'Lock', type: 'lock', editable: true, editableWhenLocked: true } }
    }
    else {
      this.editableProps = {}
    }

    this.setLock(this.lock)
    this.on('zoom', () => {
      this.resizeIcon()
    })
  }

  calcUniqueUsers() {
    this.uniqueUsers = this.comments.filter((value, index, self) => self.findIndex(x => x.userId === value.userId) === index).map(x => ({ userId: x.userId, username: x.username, firstName: x.firstName, lastName: x.lastName }))
  }

  setWidth() {
    this.set('width', 30 /* Starting width */ + 20 /* Additional width per comment  */ * (this.uniqueUsers ? this.uniqueUsers.length - 1 : 0))
  }

  resizeIcon() {
    const newZoom = this.canvas?.getZoom() || 1
    this.set('scaleX', 1 / newZoom)
    this.set('scaleY', 1 / newZoom)
  }

  setProp(prop: string, value: any, ignoreHistory: boolean = false, notifyObjectModified: boolean = true) {
    switch (prop) {
      case 'resolved':
        this.set('resolved', value.resolved)
        break
      case 'lock':
        this.set('lock', value.lock)
        this.setLock(value.lock)
        break
      default:
        console.warn('Attempting to set unsupported WbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    if (notifyObjectModified) {
      this.canvas?.fire('object:modified', { target: this, ignoreHistory })
    }
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'resolved':
        result.resolved = this.resolved
        break
      case 'lock':
        result.lock = this.lock
        break
      default:
        console.warn('Attempting to get unsupported WbObjectProp', prop)
    }
    return result
  }

  setLock(lock: boolean) {
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  addComment(content: string, mentioned: IMentionItem[], userProfile: UserProfile, localOnly: boolean) {
    this.comments.push({
      id: guid(),
      content,
      date: new Date(),
      userId: userProfile.Id,
      username: userProfile.UserName,
      firstName: userProfile.FirstName,
      lastName: userProfile.LastName,
      mentioned: mentioned.map(x => ({ value: x.value, type: x.type })),
    })
    this.localOnly = localOnly
    this.calcUniqueUsers()
    this.setWidth()
    this.dirty = true
    this.canvas?.requestRenderAll()
    this.canvas?.fire('object:modified', { target: this })
  }

  updateComment(commentId: string, content: string, mentioned: IMentionItem[]): boolean {
    const commentIndex = this.comments.findIndex(c => c.id === commentId)
    if (commentIndex >= 0) {
      this.comments[commentIndex] = {
        ...this.comments[commentIndex],
        content,
        mentioned: mentioned.map(x => ({ value: x.value, type: x.type })),
        date: new Date(),
      }

      this.dirty = true
      this.canvas?.requestRenderAll()
      this.canvas?.fire('object:modified', { target: this })
      return true
    }
    return false
  }

  migrateFromT1(catalogCode: number, discussionId: number) {
    return getDiscussionDetails(catalogCode, discussionId)
      .then((resp) => {
        if (resp.data) {
          this.title = resp.data.Title
          this.resolved = !!resp.data.Ended
          this.comments = []
          if (resp.data.Comments.length) {
            for (const comment of resp.data.Comments) {
              this.comments.push({
                id: comment.Id.toString(),
                content: comment.Comment,
                date: comment.CreatedDate,
                userId: comment.CreatedBy,
                username: comment.CreatedByUsername,
                firstName: comment.CreatedByFirstname,
                lastName: comment.CreatedByLastname,
                mentioned: [],
              })
            }
          }
          this.discussionId = undefined
          this.dirty = true
          this.canvas?.requestRenderAll()
          this.canvas?.fire('object:modified', { target: this })
        }
      })
      .catch(e => console.error(e))
  }

  setLockObject(locked: boolean) {
    this.set('lockMovementX', locked)
    this.set('lockMovementY', locked)
    this.set('lockRotation', locked)
    this.set('lockScalingFlip', locked)
    this.set('lockScalingX', locked)
    this.set('lockScalingY', locked)
    this.set('hasControls', !locked)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'resolved',
      'localOnly',
      'preventUnlock',
      'angle',
      'backgroundColor',
      'comments',
      'fill',
      'height',
      'width',
      'left',
      'top',
      // 'scaleX',
      // 'scaleY',
      'opacity',
      'version',
      'type',
      'lock',
      'link',
      'createdBy',
    ]
    return pick(this, propsToPluck)
  }

  static fromObject(opt: IWbDiscussionIconOptions, callback?: Function) {
    const object = new WbDiscussionIcon(opt)
    if (object.discussionId) {
      const userStore = useUserStore()
      if (userStore.activeCatalog) {
        object.migrateFromT1(userStore.activeCatalog.CatalogCode, object.discussionId)
          .then(() => console.log('migrated', object))
      }
    }
    return callback && callback(object)
    // return fabric.Object._fromObject(whiteboardConstants.objectTypes.discussion, object, callback) as WbDiscussionIcon
  }

  override _render(ctx: CanvasRenderingContext2D) {
    const startingX = -this.width! / 2 + 15
    ctx.fillStyle = 'blue'
    ctx.strokeStyle = 'blue'
    ctx.lineWidth = 10
    ctx.beginPath()
    ctx.arc(startingX, 0, 17, 0, Math.PI * 2)
    ctx.fill()
    if (this.uniqueUsers) {
      if (this.uniqueUsers.length > 1) {
        ctx.arc(startingX + (this.uniqueUsers.length - 1) * 20, 0, 17, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillRect(startingX, -17, (this.uniqueUsers.length - 1) * 20, 34)
      }

      for (let i = this.uniqueUsers.length - 1; i >= 0; i--) {
        ctx.fillStyle = generateUserColor(this.uniqueUsers[i].username)
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 8
        ctx.beginPath()
        ctx.arc(startingX + i * 20, 0, 11, 0, Math.PI * 2)
        ctx.stroke()
        ctx.fill()
        ctx.font = '13px Arial'
        ctx.fillStyle = 'black'
        ctx.textAlign = 'center'
        const measure = ctx.measureText(this.uniqueUsers[i].firstName[0])
        const height = measure.actualBoundingBoxAscent + measure.actualBoundingBoxDescent
        ctx.fillText(this.uniqueUsers[i].firstName[0], startingX + i * 20, height / 2)
      }
    }
  }
}

const f: any = fabric
f.WbDiscussionIcon = WbDiscussionIcon
